import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/common/pageheader/PageHeader";
import ExamPhotoCapture from "./ExamPhotoCapture";
import ExamAutoSubmitModal from "./ExamSubmitModal/ExamAutoSubmitModal";
import Loader from "../../../components/common/Loader/Loader";

import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { autosaveExamSession, resumeExamSession, startExamSession, submitExamSession } from "../../../services/examSessionsApi";
import ExamQuestionSection from "./ExamQuestionSection/ExamQuestionSection";
import styles from "./ExamSessionPage.module.css";
import ExamSubmitModal from "./ExamSubmitModal/ExamSubmitModal";

import { useExamDisqualify } from "./useExamDisqualify/useExamDisqualify";
import { useExamLock } from "../../../context/ExamLockContext";

const SESSION_KEY_PREFIX = "exam_session_";

const ExamSessionPage = () => {
    const { t } = useTranslation();
    const { examId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const roles = user?.roles || [];
    const isStudent = roles.includes("STUDENT");

    const mode = location.state?.mode || "start";
    const numericExamId = Number(examId);

    const sessionStartedKey = numericExamId ? `${SESSION_KEY_PREFIX}started_${numericExamId}` : null;
    const indexKey = numericExamId ? `${SESSION_KEY_PREFIX}idx_${numericExamId}` : null;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [examInfo, setExamInfo] = useState(null);
    const [autoSubmitted, setAutoSubmitted] = useState(false);

    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [cameraChecked, setCameraChecked] = useState(false);
    const [cameraAllowed, setCameraAllowed] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAutoSubmitModalOpen, setIsAutoSubmitModalOpen] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    // Browser-reported connectivity. Shown to the student the moment the connection drops —
    // without this the only signal was a failed autosave, which requires them to change an answer.
    const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);

    const proctoredFromRoute = location.state?.proctored === true;
    const effectiveProctored = examInfo?.proctored === true || proctoredFromRoute;

    const examEndTimeRef = useRef(null);
    const remainingSecondsRef = useRef(0);
    const saveFailedRef = useRef(false);
    // Debounce handle for OPEN free-text autosave (typing must not autosave on every keystroke).
    const textSaveTimerRef = useRef(null);
    // Mirrors for use inside window-event listeners and retry timers, which must always act on the
    // CURRENT state — a stale closure here would resend old answers over newer ones.
    const answersRef = useRef({});
    const isOfflineRef = useRef(isOffline);
    const saveInFlightRef = useRef(false);
    const saveAnswersNowRef = useRef(null);
    // Epoch ms of when the current connection loss began (offline event, or first failed save when
    // the browser never noticed). Attached to the first save that gets through afterwards, so the
    // server can put a CONNECTION_LOST entry in the attempt's event log. Null while healthy.
    // Mirrored to sessionStorage: refreshing the page is the most natural student reaction to an
    // outage, and a ref alone died with the page — the outage then left no trace in the log.
    const connectionLostAtRef = useRef(null);
    const connLostStorageKey = numericExamId ? `${SESSION_KEY_PREFIX}connlost_${numericExamId}` : null;

    const rememberConnectionLoss = (ms) => {
        connectionLostAtRef.current = ms;
        if (connLostStorageKey) {
            try { sessionStorage.setItem(connLostStorageKey, String(ms)); } catch { /* storage full/blocked — ref still works */ }
        }
    };
    const clearConnectionLoss = () => {
        connectionLostAtRef.current = null;
        if (connLostStorageKey) {
            try { sessionStorage.removeItem(connLostStorageKey); } catch { /* ignore */ }
        }
    };

    useEffect(() => {
        if (!connLostStorageKey) return;
        const raw = sessionStorage.getItem(connLostStorageKey);
        const ms = raw ? Number(raw) : NaN;
        if (Number.isFinite(ms) && ms > 0 && !connectionLostAtRef.current) {
            connectionLostAtRef.current = ms;
        }
    }, [connLostStorageKey]);

    const { disqualified } = useExamDisqualify(examInfo, user, setError, effectiveProctored);
    const { setExamLocked } = useExamLock();

    useEffect(() => {
        if (!effectiveProctored) {
            setCameraChecked(true);
            setCameraAllowed(true);
            return;
        }

        const checkCamera = async () => {
            try {
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    setError(t("pages.examSession.cameraRequired"));
                    setCameraChecked(true);
                    setCameraAllowed(false);
                    return;
                }

                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                stream.getTracks().forEach((t) => t.stop());

                setCameraAllowed(true);
                setCameraChecked(true);
            } catch {
                setError(t("pages.examSession.cameraPermissionRequired"));
                setCameraChecked(true);
                setCameraAllowed(false);
            }
        };

        setCameraChecked(false);
        setCameraAllowed(false);
        checkCamera();
    }, [effectiveProctored]);

    const buildAnswersStateFromResume = (resumeAnswers) => {
        const restored = {};

        if (Array.isArray(resumeAnswers)) {
            resumeAnswers.forEach((a) => {
                if (!a || a.questionId == null) return;
                restored[a.questionId] = {
                    selectedOptionIds: a.selectedOptionIds || [],
                    textAnswer: a.textAnswer || "",
                    booleanAnswer: typeof a.booleanAnswer === "boolean" ? a.booleanAnswer : null,
                };
            });
        } else if (resumeAnswers && typeof resumeAnswers === "object") {
            Object.values(resumeAnswers).forEach((a) => {
                if (!a || a.questionId == null) return;
                restored[a.questionId] = {
                    selectedOptionIds: a.selectedOptionIds || [],
                    textAnswer: a.textAnswer || "",
                    booleanAnswer: typeof a.booleanAnswer === "boolean" ? a.booleanAnswer : null,
                };
            });
        }

        return restored;
    };

    const calculateRemainingSeconds = React.useCallback(() => {
        if (!examEndTimeRef.current) return 0;

        const diffMs = examEndTimeRef.current - Date.now();
        return Math.max(0, Math.ceil(diffMs / 1000));
    }, []);

    const syncRemainingSeconds = React.useCallback(() => {
        const next = calculateRemainingSeconds();
        remainingSecondsRef.current = next;
        setRemainingSeconds(next);
        return next;
    }, [calculateRemainingSeconds]);

    const initializeEndTime = React.useCallback((data) => {
        if (data?.scheduledEndTime) {
            const parsed = new Date(data.scheduledEndTime).getTime();
            examEndTimeRef.current = Number.isNaN(parsed) ? null : parsed;
            return;
        }

        if (data?.startedAt && typeof data?.durationSeconds === "number") {
            const startedAtMs = new Date(data.startedAt).getTime();
            examEndTimeRef.current = Number.isNaN(startedAtMs) ? null : startedAtMs + data.durationSeconds * 1000;
            return;
        }

        if (typeof data?.remainingSeconds === "number" && data.remainingSeconds >= 0) {
            examEndTimeRef.current = Date.now() + data.remainingSeconds * 1000;
            return;
        }

        if (typeof data?.durationSeconds === "number" && data.durationSeconds >= 0) {
            examEndTimeRef.current = Date.now() + data.durationSeconds * 1000;
            return;
        }

        examEndTimeRef.current = null;
    }, []);

    const loadExamSession = React.useCallback(async () => {
        if (Number.isNaN(numericExamId)) return;

        setLoading(true);
        setError("");

        try {
            const hasLocalAttempt = sessionStartedKey && localStorage.getItem(sessionStartedKey) === "1";

            let resumed = false;

            if (hasLocalAttempt || mode !== "start") {
                try {
                    const resumeData = await resumeExamSession(numericExamId);

                    // The attempt may have been finalized while we were away (manual submit in
                    // another tab, or the server's auto-submit sweep). The DTO says so — landing in
                    // the session UI anyway started a 0-second timer whose auto-submit fired a
                    // doomed request at a finished attempt. Leave cleanly instead.
                    const finalized = ["SUBMITTED", "TIME_EXPIRED", "AUTO_SUBMITTED", "DISQUALIFIED"]
                        .includes(resumeData.status);
                    if (finalized) {
                        if (sessionStartedKey) localStorage.removeItem(sessionStartedKey);
                        if (indexKey) localStorage.removeItem(indexKey);
                        clearConnectionLoss();
                        toast.info(t("pages.examSession.alreadyFinished", {
                            defaultValue: "Bu imtahan artıq yekunlaşıb.",
                        }), { toastId: "exam-already-finished" });
                        navigate("/exams/my", { replace: true });
                        return;
                    }

                    setExamInfo({
                        attemptId: resumeData.attemptId,
                        examId: resumeData.examId,
                        examTitle: resumeData.examTitle,
                        durationSeconds: resumeData.durationSeconds || null,
                        startedAt: resumeData.startedAt,
                        scheduledEndTime: resumeData.scheduledEndTime,
                        proctored: resumeData.proctored === true || proctoredFromRoute,
                        // Where the proctoring photo counter left off — restarting it at 1 made the
                        // server log every post-resume photo as a suspicious repeated sequence.
                        lastSequenceNumber: resumeData.lastSequenceNumber || 0,
                    });

                    setQuestions(resumeData.questions || []);
                    // Restore the question the student was on (persisted per exam), clamped to range.
                    const savedIdx = indexKey ? parseInt(localStorage.getItem(indexKey) || "0", 10) : 0;
                    const qCount = (resumeData.questions || []).length;
                    setCurrentIndex(Number.isNaN(savedIdx) ? 0 : Math.min(Math.max(0, savedIdx), Math.max(0, qCount - 1)));

                    const restoredAnswers = buildAnswersStateFromResume(resumeData.answers);
                    setAnswers(restoredAnswers);

                    initializeEndTime(resumeData);
                    syncRemainingSeconds();
                    if ((resumeData.questions || []).length && !examEndTimeRef.current) {
                        setError(t("pages.examSession.loadError"));
                    }

                    resumed = true;
                } catch (err) {
                    const status = err?.response?.status;
                    const backendMsg = err?.response?.data?.message;
                    if (status !== 404 && backendMsg !== "Attempt not found") {
                        throw err;
                    }
                }
            }

            if (!resumed) {
                const data = await startExamSession({ examId: numericExamId });

                setExamInfo({
                    attemptId: data.attemptId,
                    examId: data.examId,
                    examTitle: data.examTitle,
                    durationSeconds: data.durationSeconds,
                    startedAt: data.startedAt,
                    scheduledEndTime: data.scheduledEndTime,
                    proctored: data.proctored === true || proctoredFromRoute,
                });

                setQuestions(data.questions || []);
                if (indexKey) localStorage.removeItem(indexKey); // fresh attempt starts at Q1
                setCurrentIndex(0);
                setAnswers({});

                initializeEndTime(data);
                syncRemainingSeconds();
                if ((data.questions || []).length && !examEndTimeRef.current) {
                    setError(t("pages.examSession.loadError"));
                }

                if (sessionStartedKey) {
                    localStorage.setItem(sessionStartedKey, "1");
                }
            }
        } catch (e) {
            const msg = e?.response?.data?.message || t("pages.examSession.loadError");
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [numericExamId, sessionStartedKey, indexKey, mode, proctoredFromRoute, initializeEndTime, syncRemainingSeconds, t]);

    useEffect(() => {
        if (!cameraChecked || !cameraAllowed) return;
        loadExamSession();
    }, [cameraChecked, cameraAllowed, loadExamSession]);

    useEffect(() => {
        if (disqualified) return;
        if (!examInfo?.examId) return;
        if (!examEndTimeRef.current) return;

        syncRemainingSeconds();

        const intervalId = window.setInterval(() => {
            const next = syncRemainingSeconds();
            if (next <= 0) {
                window.clearInterval(intervalId);
            }
        }, 1000);

        const handleVisibilityOrFocus = () => {
            syncRemainingSeconds();
        };

        document.addEventListener("visibilitychange", handleVisibilityOrFocus);
        window.addEventListener("focus", handleVisibilityOrFocus);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
            window.removeEventListener("focus", handleVisibilityOrFocus);
        };
    }, [examInfo?.examId, disqualified, syncRemainingSeconds]);

    const handleSubmit = React.useCallback(
        async (isAuto = false) => {
            if (disqualified) {
                setError(t("pages.examSession.cannotSubmitDisqualified"));
                return;
            }

            if (!examInfo || !examInfo.examId) return;

            const studentNumber = user?.studentNumber || null;
            if (!studentNumber) {
                const msg = t("pages.examSession.studentNumberNotFound");
                setError(msg);
                return;
            }

            setSubmitting(true);
            setError("");

            // Cancel any pending debounced text autosave; the submit below carries the latest text.
            if (textSaveTimerRef.current) {
                clearTimeout(textSaveTimerRef.current);
                textSaveTimerRef.current = null;
            }

            // A connection loss that has not reached the server yet only travels inside an
            // autosave — flush it now, or "reconnect and submit straight away" would leave no
            // trace of the outage in the attempt log. Best effort: a failure must not block
            // submission, and the submit itself proves the connection is back.
            if (connectionLostAtRef.current && Object.keys(answers).length > 0) {
                try {
                    await saveAnswersNowRef.current?.(answers);
                } catch {
                    // saveAnswersNow handles its own failures; never stall the submit.
                }
            }

            const preparedAnswers = Object.entries(answers).map(([questionId, value]) => ({
                questionId: Number(questionId),
                selectedOptionIds: value.selectedOptionIds || [],
                textAnswer: value.textAnswer || null,
            }));

            const latestRemainingSeconds = calculateRemainingSeconds();
            const elapsedSeconds =
                typeof examInfo.durationSeconds === "number"
                    ? Math.max(0, examInfo.durationSeconds - latestRemainingSeconds)
                    : null;

            const payload = {
                examId: examInfo.examId,
                studentNumber,
                answers: preparedAnswers,
                ...(elapsedSeconds !== null ? { elapsedSeconds } : {}),
            };

            try {
                remainingSecondsRef.current = latestRemainingSeconds;
                setRemainingSeconds(latestRemainingSeconds);

                await submitExamSession(payload);

                // The sitting is over: clear the per-exam local state so the next visit doesn't
                // force the resume path against a finalized attempt, and drop any still-undelivered
                // outage marker (its attempt just ended).
                if (sessionStartedKey) localStorage.removeItem(sessionStartedKey);
                if (indexKey) localStorage.removeItem(indexKey);
                clearConnectionLoss();

                if (isAuto) {
                    setIsConfirmModalOpen(false);
                    setAutoSubmitted(true);
                    setIsAutoSubmitModalOpen(true);
                } else {
                    toast.success(t("pages.examSession.submitSuccess"));
                    navigate("/exams/my");
                }
            } catch (e) {
                const msg = e?.response?.data?.message || t("pages.examSession.submitError");
                setError(msg);
            } finally {
                setSubmitting(false);
            }
        },
        [disqualified, examInfo, user, answers, calculateRemainingSeconds, navigate]
    );

    const autoSubmitOnTimeout = React.useCallback(() => {
        if (autoSubmitted) return;
        if (disqualified) return;

        setAutoSubmitted(true);
        handleSubmit(true);
    }, [autoSubmitted, disqualified, handleSubmit]);

    useEffect(() => {
        // Only auto-submit if the timer was genuinely initialized — a malformed payload
        // (no resolvable end time) would otherwise leave remainingSeconds=0 and fire instantly.
        if (!examEndTimeRef.current) return;
        if (remainingSeconds === 0 && examInfo?.examId) {
            autoSubmitOnTimeout();
        }
    }, [remainingSeconds, examInfo, autoSubmitOnTimeout]);

    // The sitting is live: the single answer to "is this student still sitting the exam right now".
    // The unload warning, the sidebar lock and the proctoring camera all key off it, so none of them
    // can disagree about when the exam ended.
    const examInProgress = !!examInfo?.examId && questions.length > 0 && !autoSubmitted && !disqualified;

    // Warn before leaving an in-progress exam (refresh / close / browser back).
    useEffect(() => {
        if (!examInProgress) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [examInProgress]);

    // Lock the sidebar for exactly as long as the sitting is live. The cleanup runs on unmount too,
    // which is what releases the menu after submit, disqualification or navigating away.
    useEffect(() => {
        setExamLocked(examInProgress);
        return () => setExamLocked(false);
    }, [examInProgress, setExamLocked]);

    useEffect(() => {
        answersRef.current = answers;
    }, [answers]);

    // Resend the CURRENT full answer state (autosave always carries everything, so one success
    // heals every save that failed while the connection was down). Guarded against overlapping an
    // in-flight save — user-triggered saves always run, only these background resends yield.
    const resendAnswers = React.useCallback(() => {
        if (disqualified || autoSubmitted) return;
        if (saveInFlightRef.current) return;
        if (!Object.keys(answersRef.current).length) return;
        saveAnswersNowRef.current?.(answersRef.current);
    }, [disqualified, autoSubmitted]);

    // Connection loss: banner immediately (not only after a save fails), resync on restore.
    useEffect(() => {
        const handleOffline = () => {
            isOfflineRef.current = true;
            setIsOffline(true);
            if (!connectionLostAtRef.current) {
                rememberConnectionLoss(Date.now());
            }
        };
        // 'online' fires when the network INTERFACE is up, which is routinely a second or two
        // before DNS/sockets actually work — a single immediate resend often fails right after
        // reconnecting. Follow up at 3s and 8s while anything is still unsaved or unreported;
        // each attempt no-ops once a save has gone through.
        const followUps = [];
        const handleOnline = () => {
            isOfflineRef.current = false;
            setIsOffline(false);
            resendAnswers();
            for (const delay of [3000, 8000]) {
                followUps.push(window.setTimeout(() => {
                    if (connectionLostAtRef.current || saveFailedRef.current) {
                        resendAnswers();
                    }
                }, delay));
            }
        };
        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);
        return () => {
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
            followUps.forEach((id) => window.clearTimeout(id));
        };
    }, [resendAnswers]);

    // navigator.onLine can stay true while the server is unreachable (router up, internet down,
    // backend down) — so a failed save also retries on a timer until one goes through. Paused
    // while the browser KNOWS it is offline; the 'online' handler resends immediately instead.
    useEffect(() => {
        if (!saveFailed || isOffline) return;
        const id = window.setInterval(() => resendAnswers(), 15000);
        return () => window.clearInterval(id);
    }, [saveFailed, isOffline, resendAnswers]);

    const saveAnswersNow = async (updatedAnswers) => {
        if (!examInfo || !examInfo.examId) return;
        if (!questions.length) return;

        const studentNumber = user?.studentNumber || null;
        if (!studentNumber) return;

        const preparedAnswers = Object.entries(updatedAnswers).map(([questionId, value]) => ({
            questionId: Number(questionId),
            selectedOptionIds: value.selectedOptionIds || [],
            textAnswer: value.textAnswer || null,
            booleanAnswer: typeof value.booleanAnswer === "boolean" ? value.booleanAnswer : null,
        }));

        const latestRemainingSeconds = calculateRemainingSeconds();

        // Move the pending connection-loss report out of the ref so exactly ONE save carries it —
        // a user-triggered save can overlap a background resend, and both reporting the same gap
        // would write duplicate log rows. Restored on failure so the report survives until some
        // save gets through; the server logs it into the attempt's event log.
        const connectionLostAtMs = connectionLostAtRef.current;
        connectionLostAtRef.current = null;

        const payload = {
            examId: examInfo.examId,
            studentNumber,
            answers: preparedAnswers,
            remainingSeconds: latestRemainingSeconds,
            ...(connectionLostAtMs
                ? { connectionLostAtMs, connectionRestoredAtMs: Date.now() }
                : {}),
        };

        saveInFlightRef.current = true;
        try {
            remainingSecondsRef.current = latestRemainingSeconds;
            setRemainingSeconds(latestRemainingSeconds);
            await autosaveExamSession(payload);
            if (connectionLostAtMs) {
                // The outage report reached the server — drop the sessionStorage mirror too.
                clearConnectionLoss();
            }
            if (saveFailedRef.current) {
                saveFailedRef.current = false;
                setSaveFailed(false);
                // Earlier saves failed and this one carried the full answer state — tell the
                // student they are whole again, mirroring the failure toast they saw.
                toast.success(t("pages.examSession.autosaveRecovered"));
            }
        } catch {
            // Keep the EARLIEST loss start: a failed report attempt means the outage is still on,
            // and any fresher timestamp would shrink the logged gap. Mint a new start only when
            // this failure is the first sign of trouble (connection died without an offline event).
            if (connectionLostAtMs) {
                rememberConnectionLoss(connectionLostAtRef.current
                    ? Math.min(connectionLostAtRef.current, connectionLostAtMs)
                    : connectionLostAtMs);
            } else if (!connectionLostAtRef.current) {
                rememberConnectionLoss(Date.now());
            }
            // Surface the failure (once) instead of silently dropping the answer. Skip the toast
            // when the browser already knows it is offline — the banner explains it, and the
            // retry timer would otherwise toast every attempt.
            if (!saveFailedRef.current) {
                saveFailedRef.current = true;
                setSaveFailed(true);
                if (!isOfflineRef.current) {
                    toast.error(t("pages.examSession.autosaveFailed"));
                }
            }
        } finally {
            saveInFlightRef.current = false;
        }
    };
    saveAnswersNowRef.current = saveAnswersNow;

    const handleOptionChange = (questionId, optionId, multiple) => {
        // Cancel any pending debounced TEXT save: the immediate save below carries the full current
        // state including that text. Letting the debounce fire afterwards posted the OLDER snapshot
        // — without this option click — over the newer one (type in an OPEN question, click an
        // option within a second: the option vanished from the server copy).
        if (textSaveTimerRef.current) {
            clearTimeout(textSaveTimerRef.current);
            textSaveTimerRef.current = null;
        }
        setAnswers((prev) => {
            const existing = prev[questionId] || {
                selectedOptionIds: [],
                textAnswer: "",
                booleanAnswer: null,
            };

            let updatedSelected = [];

            if (multiple) {
                const exists = existing.selectedOptionIds.includes(optionId);
                updatedSelected = exists ? existing.selectedOptionIds.filter((id) => id !== optionId) : [...existing.selectedOptionIds, optionId];
            } else {
                updatedSelected = [optionId];
            }

            const updated = {
                ...prev,
                [questionId]: {
                    ...existing,
                    selectedOptionIds: updatedSelected,
                },
            };

            saveAnswersNow(updated);
            return updated;
        });
    };

    const handleTextChange = (questionId, text) => {
        setAnswers((prev) => {
            const existing = prev[questionId] || {
                selectedOptionIds: [],
                textAnswer: "",
                booleanAnswer: null,
            };

            const updated = {
                ...prev,
                [questionId]: {
                    ...existing,
                    textAnswer: text,
                },
            };

            // Debounce the autosave: typing fires this on every keystroke, so save ~1s after the
            // student pauses instead of per character (avoids flooding autosave / the per-user limit).
            if (textSaveTimerRef.current) clearTimeout(textSaveTimerRef.current);
            textSaveTimerRef.current = setTimeout(() => {
                textSaveTimerRef.current = null;
                saveAnswersNow(updated);
            }, 1000);

            return updated;
        });
    };

    const formatTime = (totalSeconds) => {
        if (totalSeconds == null || totalSeconds < 0) return "00:00";
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const m = String(minutes).padStart(2, "0");
        const s = String(seconds).padStart(2, "0");
        return hours > 0 ? `${hours}:${m}:${s}` : `${m}:${s}`;
    };

    const handleGoToQuestion = (index) => {
        if (index < 0 || index >= questions.length) return;
        setCurrentIndex(index);
        if (indexKey) localStorage.setItem(indexKey, String(index));
    };

    const unansweredCount = questions.reduce((n, q) => {
        const a = answers[q.id];
        const answered = q.type === "OPEN"
            ? !!(a && typeof a.textAnswer === "string" && a.textAnswer.trim().length > 0)
            : !!(a && a.selectedOptionIds && a.selectedOptionIds.length > 0);
        return n + (answered ? 0 : 1);
    }, 0);

    if (cameraChecked && !cameraAllowed) {
        return (
            <div className={styles.container}>
                <PageHeader title={t("pages.examSession.title")} subtitle={t("pages.examSession.cameraPermissionPrompt")} />
                {error && <div className={styles.error}>{error}</div>}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <PageHeader title={examInfo?.examTitle || t("pages.examSession.title")} subtitle={t("pages.examSession.subtitle")} />

            <div className={styles.topBar}>
                <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>
                    <FiArrowLeft />
                    <span>{t("pages.examSession.back")}</span>
                </button>

                <div className={styles.timerBox}>
                    {(isOffline || saveFailed) && (
                        <span style={{ color: "#dc2626", fontSize: 12, fontWeight: 600, marginRight: 8 }}>
                            {isOffline
                                ? t("pages.examSession.offlineBanner")
                                : t("pages.examSession.autosaveFailedShort")}
                        </span>
                    )}
                    <span>{t("pages.examSession.remainingTime")}</span>
                    <span className={styles.timerValue}>{formatTime(remainingSeconds)}</span>
                </div>
            </div>

            {/* Only while the sitting is live. Disqualification and time-up leave this page mounted
                (one shows an error, the other a modal), and the server drops the Redis session the
                moment either happens — so without examInProgress the camera kept shooting every
                5-80s and every upload came back 404 error.exam.session.notFound. */}
            {effectiveProctored && cameraAllowed && examInfo?.attemptId && examInProgress && (
                <ExamPhotoCapture
                    examId={examInfo.examId}
                    studentNumber={user?.studentNumber}
                    initialSequenceNumber={examInfo?.lastSequenceNumber || 0}
                />
            )}

            {error && <div className={styles.error}>{error}</div>}

            {loading ? (
                <Loader fullscreen />
            ) : !questions.length ? (
                <div className={styles.infoText}>{t("pages.examSession.noQuestions")}</div>
            ) : !disqualified ? (
                <ExamQuestionSection
                    questions={questions}
                    currentIndex={currentIndex}
                    answers={answers}
                    onGoToQuestion={handleGoToQuestion}
                    onOptionChange={handleOptionChange}
                    onTextChange={handleTextChange}
                    isStudent={isStudent}
                />
            ) : (
                <></>
            )}

            <div className={styles.actionsBar}>
                <button
                    type="button"
                    className={styles.submitButton}
                    onClick={() => setIsConfirmModalOpen(true)}
                    disabled={submitting || loading || !questions.length || disqualified}
                >
                    <FiCheckCircle />
                    <span>{submitting ? t("pages.examSession.submitting") : t("pages.examSession.finishExam")}</span>
                </button>
            </div>

            <ExamSubmitModal
                isOpen={isConfirmModalOpen}
                title={t("pages.examSession.finishExam")}
                message={
                    unansweredCount > 0
                        ? `${t("pages.examSession.finishConfirm")} ${t("pages.examSession.unansweredWarning", { count: unansweredCount })}`
                        : t("pages.examSession.finishConfirm")
                }
                confirmText={t("common.confirm")}
                cancelText={t("common.cancel")}
                onConfirm={() => {
                    setIsConfirmModalOpen(false);
                    handleSubmit(false);
                }}
                onCancel={() => setIsConfirmModalOpen(false)}
                showCancel
            />

            <ExamAutoSubmitModal
                isOpen={isAutoSubmitModalOpen}
                title={t("pages.examSession.timeUpTitle")}
                message={t("pages.examSession.timeUpMessage")}
                confirmText={t("common.close")}
                onConfirm={() => {
                    setIsAutoSubmitModalOpen(false);
                    navigate("/exams/my");
                }}
            />
        </div>
    );
};

export default ExamSessionPage;